import { useState, useEffect } from "react";
import AnimatedSection from "@/components/base/AnimatedSection";
import { broadcastItems, pressItems } from "@/mocks/home";

function SectionLabel({ label, description }: { label: string; description: string }) {
  return (
    <div className="flex items-center gap-3 mb-8 md:mb-10">
      <span className="w-1 h-7 md:h-8 rounded-full bg-accent-500 shrink-0" />
      <div>
        <h3 className="font-display text-xl md:text-2xl font-bold text-foreground-950">
          {label}
        </h3>
        <p className="text-xs md:text-sm text-foreground-500 mt-0.5">
          {description}
        </p>
      </div>
    </div>
  );
}

export default function MediaSection() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    if (!activeVideo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveVideo(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeVideo]);

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-accent-500 text-sm font-medium tracking-widest mb-2">MEDIA</p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-primary-500">
            방송출연 및 언론보도
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            부동산·형사전문 이승환 변호사의 방송 출연 및 언론보도 활동을 소개합니다
          </p>
        </div>

        <SectionLabel label="방송출연" description="TV 뉴스·시사 프로그램 법률 자문 인터뷰" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {broadcastItems.map((item, idx) => (
            <AnimatedSection key={idx} delay={idx * 120}>
              <div className="bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 group h-full flex flex-col">
                <div className="relative aspect-video bg-primary-900 shrink-0">
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-primary-950/40 group-hover:bg-primary-950/30 transition-colors" />
                  {item.videoUrl && (
                    <button
                      onClick={() => setActiveVideo(item.videoUrl)}
                      className="absolute inset-0 flex items-center justify-center cursor-pointer"
                      aria-label={`${item.title} 재생`}
                    >
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500/90 hover:bg-accent-500 flex items-center justify-center transition-colors shadow-lg">
                        <i className="ri-play-fill text-foreground-950 text-2xl md:text-3xl ml-1" />
                      </div>
                    </button>
                  )}
                  {!item.videoUrl && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="px-4 py-2 bg-primary-950/60 rounded-full text-white/80 text-sm">
                        영상 준비 중
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <span className="inline-block self-start px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-600 border border-primary-100 mb-3">
                    {item.subtitle}
                  </span>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                    {item.description}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <div className="mt-16 md:mt-20">
          <SectionLabel label="언론보도" description="주요 언론 보도 기사 및 칼럼" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            {pressItems.map((item, idx) => (
              <AnimatedSection key={idx} delay={idx * 80}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex items-center gap-4 md:gap-5 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 p-3 md:p-4 h-full"
                >
                  <div className="w-24 md:w-32 h-16 md:h-20 rounded-lg overflow-hidden shrink-0 bg-primary-900">
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      title={`${item.outlet} - ${item.title}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-secondary-100 text-secondary-900 mb-2">
                      {item.outlet}
                    </span>
                    <h4 className="text-sm md:text-[15px] font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h4>
                  </div>
                  <span className="shrink-0 w-9 h-9 rounded-full bg-background-50 border border-background-200 flex items-center justify-center text-foreground-500 group-hover:bg-accent-500 group-hover:text-foreground-950 group-hover:border-accent-500 transition-colors">
                    <i className="ri-arrow-right-up-line text-lg" />
                  </span>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>

      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-primary-950/80 backdrop-blur-sm"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute -top-12 right-0 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              aria-label="닫기"
            >
              <i className="ri-close-line text-2xl" />
            </button>
            <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
              <video
                src={activeVideo}
                controls
                autoPlay
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}